import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { GET_USERS_QUERY } from "components/Tasks/userQueries";
import { getFullName } from "encamp-shared/src/utils/name";
import { AssigneePickerFragment } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { uniqBy } from "lodash-es";
import { useMemo, useState } from "react";

type AssigneeMultiSelectProps = {
  value: string[];
  onChange: (userIds: string[]) => void;
};

const USERS_SORT = [{ field: "name", sort: "asc" }];
const userLabel = (user: AssigneePickerFragment) => getFullName(user.person) || user.email;

/** Picks users by id, showing the chosen ones as chips. */
export function AssigneeMultiSelect({ value, onChange }: AssigneeMultiSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [search] = useDebounce(inputValue, 300);

  // Saved filters only hold ids, so look up the selected users to label their chips.
  const { data: selectedData } = useQuery(GET_USERS_QUERY, {
    variables: {
      search: `id:${value.join("|")}`,
      page: 0,
      pageSize: value.length,
      sort: USERS_SORT,
    },
    skip: value.length === 0,
  });
  const { data: optionsData, loading } = useQuery(GET_USERS_QUERY, {
    variables: { search, page: 0, pageSize: 20, sort: USERS_SORT },
  });

  const selectedUsers = useMemo(() => {
    const known = uniqBy(
      [...(selectedData?.users.items ?? []), ...(optionsData?.users.items ?? [])],
      "id"
    );
    return value.flatMap((id) => known.find((user) => user.id === id) ?? []);
  }, [value, selectedData, optionsData]);

  return (
    <Autocomplete
      multiple
      size="small"
      options={optionsData?.users.items ?? []}
      value={selectedUsers}
      loading={loading}
      filterOptions={(options) => options}
      getOptionLabel={userLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      onChange={(_, users) => {
        // Keep ids whose users haven't loaded yet; they aren't shown, so can't have been removed.
        const unresolved = value.filter((id) => !selectedUsers.some((user) => user.id === id));
        onChange([...unresolved, ...users.map((user) => user.id)]);
      }}
      renderInput={(params) => (
        <TextField {...params} label="Assignee" placeholder={value.length === 0 ? "Anyone" : ""} />
      )}
    />
  );
}
